import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { Calendar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isTomorrow } from 'date-fns';
import { useBusiness } from '../../../context/BusinessContext';
import { useThemeStyles } from '../../../theme/useThemeStyles';
import api from '../../../utils/api';
import DashboardCard from '../DashboardCard';

function UpcomingAppointmentsCard({ dashboardContext }) {
  const navigate = useNavigate();
  const styles = useThemeStyles();
  const { selectedBusiness } = useBusiness();
  const [appointments, setAppointments] = useState({ items: [], loading: true, error: null });

  const businessId = selectedBusiness?.business?._id;

  const fetchAppointments = useCallback(async () => {
    if (!businessId) return;

    try {
      setAppointments(prev => ({ ...prev, loading: true, error: null }));
      const weekStart = dashboardContext?.weekStartMs ? new Date(dashboardContext.weekStartMs) : new Date();
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const response = await api.get('/api/unified-bookings', {
        params: {
          type: 'supplier',
          businessId,
          startDate: weekStart.toISOString(),
          endDate: weekEnd.toISOString(),
          status: 'confirmed',
        },
      });

      const bookings = response.data.bookings || [];
      const userMatchIds = Array.isArray(dashboardContext?.userMatchIds)
        ? dashboardContext.userMatchIds.map((id) => String(id))
        : [];
      const isMyOverview = dashboardContext?.overviewScope === 'my';
      const appointmentsList = bookings
        .filter(booking => {
          if (!booking.startTime) return false;
          const startTime = new Date(booking.startTime);
          if (startTime < weekStart || startTime > weekEnd) return false;
          if (!isMyOverview || userMatchIds.length === 0) return true;
          const assignments = Array.isArray(booking.assignedTeamMembers) ? booking.assignedTeamMembers : [];
          return assignments.some((assignment) => {
            const assignmentIds = [
              assignment?.member,
              assignment?.staffMember,
              assignment?.member?.user?._id,
              assignment?.member?.user?.id,
              assignment?.staffMember?.user?._id,
              assignment?.staffMember?.user?.id,
              assignment?.member?._id,
              assignment?.member?.id,
              assignment?.staffMember?._id,
              assignment?.staffMember?.id,
            ].filter(Boolean);
            return assignmentIds.some((id) => userMatchIds.includes(String(id)));
          });
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .slice(0, 7)
        .map(booking => ({
          id: booking._id,
          startTime: booking.startTime,
          clientName: booking.client
            ? `${booking.client.firstName || ''} ${booking.client.lastName || ''}`.trim() || 'Unknown'
            : 'Unknown',
          serviceType: booking.serviceType?.name || 'Unknown Service',
          status: booking.status,
        }));

      setAppointments({ items: appointmentsList, loading: false, error: null });
    } catch (error) {
      setAppointments({ items: [], loading: false, error: 'Failed to load appointments' });
    }
  }, [businessId, dashboardContext?.overviewScope, dashboardContext?.userMatchIds, dashboardContext?.weekStartMs]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const getDateLabel = (startTime) => {
    const date = new Date(startTime);
    if (isToday(date)) return `Today, ${format(date, 'h:mm a')}`;
    if (isTomorrow(date)) return `Tomorrow, ${format(date, 'h:mm a')}`;
    return `${format(date, 'EEE')}, ${format(date, 'h:mm a')}`;
  };

  return (
    <DashboardCard
      title="Upcoming Appointments"
      icon={<Calendar size={18} weight="regular" />}
      loading={appointments.loading}
      error={appointments.error}
      isEmpty={!appointments.loading && appointments.items.length === 0}
      emptyMessage="Nothing in the diary for this week. When a booking is confirmed, it will show here."
      emptyIcon={Calendar}
      menuItems={[{ label: 'View all', onClick: () => navigate('/supplier/bookings?view=calendar') }]}
      iconBackgroundColor="rgba(128, 90, 213, 0.12)"
      iconColour="rgba(128, 90, 213, 1)"
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {appointments.items.length > 0 && (
        <>
          <List disablePadding>
            {appointments.items.map((appointment) => (
              <React.Fragment key={appointment.id}>
                <ListItem
                  sx={{
                    px: 0.5,
                    py: 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.1,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/supplier/bookings?view=calendar&booking=${appointment.id}`);
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      minWidth: 48,
                      minHeight: 48,
                      height: 'auto',
                      py: 0.8,
                      borderRadius: '10px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      backgroundColor: 'rgba(255, 255, 255, 0.72)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1 }}>
                      {format(new Date(appointment.startTime), 'MMM').toUpperCase()}
                    </Typography>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 500, lineHeight: 1, mt: -0.05 }}>
                      {format(new Date(appointment.startTime), 'd')}
                    </Typography>
                  </Box>

                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {appointment.clientName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {appointment.serviceType}
                      </Typography>
                    }
                  />

                  <Chip
                    label={getDateLabel(appointment.startTime)}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(249, 198, 157, 0.42)',
                      borderColor: 'transparent',
                      color: 'rgba(0, 0, 0, 0.86)',
                      fontWeight: 500,
                      borderRadius: '8px',
                      '& .MuiChip-label': { px: 1.2 },
                    }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.5, fontWeight: 500 }}
          >
            {appointments.items.length} {appointments.items.length === 1 ? 'appointment' : 'appointments'} scheduled
          </Typography>
        </>
      )}
    </DashboardCard>
  );
}

export default UpcomingAppointmentsCard;
